import type { ButtonHTMLAttributes, MouseEvent, ReactElement, ReactNode } from 'react';
import { Icon, type IconName } from '../Icon/Icon';
import styles from './Button.module.scss';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  icon?: IconName;
  loading?: boolean;
  block?: boolean;
  children?: ReactNode;
}

export function Button({ variant = 'secondary', size = 'md', icon, loading = false, block = false, children, className, disabled, type = 'button', ...rest }: ButtonProps): ReactElement {
  const classes = [styles.button, styles[variant], size === 'sm' ? styles.small : '', block ? styles.block : '', className ?? ''].join(' ');
  return (
    <button type={type} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : icon ? <Icon name={icon} size={size === 'sm' ? 14 : 16} /> : null}
      {children !== undefined && children !== null ? <span className={styles.label}>{children}</span> : null}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  /** Обязательная подпись для экранных читалок и подсказки. */
  label: string;
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
}

export function IconButton({ icon, label, variant = 'ghost', size = 'md', className, type = 'button', ...rest }: IconButtonProps): ReactElement {
  const classes = [styles.button, styles[variant], styles.iconOnly, size === 'sm' ? styles.small : '', className ?? ''].join(' ');
  return (
    <button type={type} className={classes} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={size === 'sm' ? 14 : 18} />
    </button>
  );
}

/** Ссылка, выглядящая как кнопка (скачивание, внешний переход). */
export function LinkButton({
  href,
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  download,
  external,
  onNavigate,
}: {
  href: string;
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  icon?: IconName;
  children: ReactNode;
  download?: boolean | string;
  external?: boolean;
  /** Переход внутри приложения без перезагрузки; Ctrl/Cmd-клик по-прежнему открывает вкладку. */
  onNavigate?(): void;
}): ReactElement {
  const classes = [styles.button, styles[variant], size === 'sm' ? styles.small : ''].join(' ');
  const handle = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!onNavigate || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onNavigate();
  };
  return (
    <a
      className={classes}
      href={href}
      onClick={handle}
      download={download === true ? '' : download || undefined}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
    >
      {icon ? <Icon name={icon} size={size === 'sm' ? 14 : 16} /> : null}
      <span className={styles.label}>{children}</span>
    </a>
  );
}

/** Продолжение постраничного списка: видна, пока есть ещё записи. */
export function ShowMoreButton({ visible, loading, onClick, children }: { visible: boolean; loading: boolean; onClick: () => void; children: ReactNode }): ReactElement | null {
  if (!visible) return null;
  return (
    <div className={styles.showMore}>
      <Button onClick={onClick} loading={loading}>
        {children}
      </Button>
    </div>
  );
}
